import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { endorsements, expertiseAreas } from '../data/expertise';
import { Reveal } from '../components/Reveal';
import { SectionHeading } from '../components/SectionHeading';
export function Expertise() {
  const [activeId, setActiveId] = useState(expertiseAreas[0].id);
  const active = expertiseAreas.find(a => a.id === activeId) ?? expertiseAreas[0];
  const topEndorsed = endorsements.slice(0, 6);
  const otherEndorsed = endorsements.slice(6);
  const handleKey = (e: React.KeyboardEvent, index: number) => {
    if (!['ArrowDown', 'ArrowUp', 'ArrowRight', 'ArrowLeft'].includes(e.key)) return;
    e.preventDefault();
    const dir = e.key === 'ArrowDown' || e.key === 'ArrowRight' ? 1 : -1;
    const next = (index + dir + expertiseAreas.length) % expertiseAreas.length;
    setActiveId(expertiseAreas[next].id);
    document.getElementById(`tab-${expertiseAreas[next].id}`)?.focus();
  };
  return <>
      <section aria-labelledby="expertise-title" className="bg-paper-deep py-20 md:py-28" data-id="element-256" data-mp="1x6saoyynhuko">
        <div className="mx-auto max-w-7xl px-6 lg:px-10" data-id="element-257" data-mp="1s7fhlwh6rr9n">
          <SectionHeading id="expertise-title" label="04 · Key expertise & competencies" title="Where technology, process and market meet." data-id="element-258" data-mp="1s7z0pgikolp9" />

          <Reveal className="mt-14 grid gap-10 lg:grid-cols-12 lg:gap-16" data-id="element-259" data-mp="1j70qgghjjhy8">
            <div role="tablist" aria-label="Areas of expertise" className="flex gap-2 overflow-x-auto pb-2 lg:col-span-5 lg:flex-col lg:gap-0 lg:overflow-visible lg:border-t lg:border-line lg:pb-0" data-id="element-260" data-mp="1j8u2kbaxaudm">
              {expertiseAreas.map((area, i) => {
              const selected = area.id === activeId;
              return <button key={area.id} id={`tab-${area.id}`} role="tab" type="button" aria-selected={selected} aria-controls={`panel-${area.id}`} tabIndex={selected ? 0 : -1} onClick={() => setActiveId(area.id)} onKeyDown={e => handleKey(e, i)} className={`shrink-0 whitespace-nowrap rounded-sm border px-4 py-2.5 text-left text-sm transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-bronze lg:whitespace-normal lg:rounded-none lg:border-0 lg:border-b lg:border-line lg:px-0 lg:py-5 lg:font-serif lg:text-xl ${selected ? 'border-ink bg-ink text-paper lg:bg-transparent lg:text-ink' : 'border-line text-slate-text hover:text-ink'}`} data-id="element-261" data-mp="10dwxkxc5d94o" data-mpi={i}>
                    <span className="flex items-center gap-4" data-id="element-262" data-mp="uth3x827y15c">
                      <span className={`hidden h-px transition-[width,background-color] duration-200 lg:block ${selected ? 'w-8 bg-bronze' : 'w-0 bg-transparent'}`} aria-hidden="true" data-id="element-263" data-mp="urmjduuv0b1s" />
                      {area.name}
                    </span>
                  </button>;
            })}
            </div>

            <div className="lg:col-span-7" data-id="element-264" data-mp="32zhyd6xlwsu6">
              <AnimatePresence mode="wait" data-id="element-265" data-mp="3hqrwszjeetw0">
                <motion.div key={active.id} id={`panel-${active.id}`} role="tabpanel" aria-labelledby={`tab-${active.id}`} initial={{
                opacity: 0,
                y: 8
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -8
              }} transition={{
                duration: 0.2,
                ease: [0.23, 1, 0.32, 1]
              }} className="rounded-sm bg-paper p-8 md:p-10" data-id="element-266" data-mp="3hppaz60xnhmy">
                  <h3 className="font-serif text-3xl text-ink" data-id="element-267" data-mp="2tzmjos4uefpe">{active.name}</h3>
                  <p className="mt-4 text-base leading-relaxed text-slate-text" data-id="element-268" data-mp="daz4gx1yx7z7">{active.summary}</p>
                  <ul className="mt-8 flex flex-wrap gap-2.5" data-id="element-269" data-mp="99x7ynzfra0x">
                    {active.skills.map(skill => <li key={skill} className="rounded-sm border border-line px-3.5 py-2 text-sm text-ink" data-id="element-270" data-mp="14moheo3tu3mu">
                        {skill}
                      </li>)}
                  </ul>
                  <div className="mt-10 border-t border-line pt-6" data-id="element-271" data-mp="1dnwg37a996hg">
                    <p className="text-sm font-semibold text-ink" data-id="element-272" data-mp="1im7pxi0jvgzo">Demonstrated in</p>
                    <p className="mt-2 text-sm leading-relaxed text-slate-text" data-id="element-273" data-mp="1niogeiv827fx">{active.evidence.join(' · ')}</p>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </Reveal>
        </div>
      </section>

      <section aria-labelledby="endorsed-title" className="bg-paper py-20 md:py-28" data-id="element-274" data-mp="21b4inhgau1fs">
        <div className="mx-auto max-w-7xl px-6 lg:px-10" data-id="element-275" data-mp="21cytu41rqf6o">
          <Reveal className="max-w-3xl" data-id="element-276" data-mp="26bihc8w0fz15">
            <h2 id="endorsed-title" className="font-serif text-3xl text-ink md:text-4xl" data-id="element-277" data-mp="268e7auvv692k">
              Endorsed skills
            </h2>
            <p className="mt-3 text-base text-slate-text" data-id="element-278" data-mp="3vrmh7ogfq1hd">
              Skills endorsed by colleagues and professional contacts on LinkedIn. Spanish entries are shown with an English translation.
            </p>
          </Reveal>

          <ul className="mt-12 grid gap-px border border-line bg-line sm:grid-cols-2 lg:grid-cols-3" data-id="element-279" data-mp="3ltos6yqr40np">
            {topEndorsed.map(e => <li key={e.name} className="flex flex-col bg-paper p-7" data-id="element-280" data-mp="ic6p14lxbhtm">
                <span className="font-serif text-5xl leading-none text-ink" data-id="element-281" data-mp="iej1j28wwzza">{e.count}</span>
                <span className="mt-1 text-xs text-slate-text" data-id="element-282" data-mp="de52uat7b2dx">endorsements</span>
                <p className="mt-5 text-lg font-semibold text-ink" data-id="element-283" data-mp="8l29a2ofbsxq">{e.name}</p>
                {e.translation && <p className="text-sm text-slate-text" data-id="element-284" data-mp="3msa7gzkw86u">{e.translation}</p>}
                {e.note && <p className="mt-auto pt-4 text-xs leading-relaxed text-bronze" data-id="element-285" data-mp="113zfar9lyoxn">{e.note}</p>}
              </li>)}
          </ul>

          <ul className="mt-10 grid gap-x-10 sm:grid-cols-2 lg:grid-cols-3" data-id="element-286" data-mp="wa4pr77bnfqn">
            {otherEndorsed.map(e => <li key={e.name} className="flex items-baseline justify-between gap-4 border-b border-line py-3.5" data-id="element-287" data-mp="2vj684swqn75h">
                <div className="min-w-0" data-id="element-288" data-mp="2gus083kgssi2">
                  <p className="text-sm font-medium text-ink" data-id="element-289" data-mp="2gtqjpjkojdsy">{e.name}</p>
                  {e.translation && <p className="text-xs text-slate-text" data-id="element-290" data-mp="2lrs4qa1o1n72">{e.translation}</p>}
                  {e.note && <p className="mt-1 text-xs text-bronze" data-id="element-291" data-mp="27ssgh3i2fabf">{e.note}</p>}
                </div>
                <span className="shrink-0 text-sm tabular-nums text-slate-text" data-id="element-292" data-mp="2bw89zqyu8ujw">{e.count}</span>
              </li>)}
          </ul>
        </div>
      </section>
    </>;
}